import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, CreditCard, Calendar, X, ExternalLink, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const API = API_URL;

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [hubSerial, setHubSerial] = useState('');

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      console.log('Success:', 'Abonnement activé avec succès !');
    }
    if (searchParams.get('canceled') === 'true') {
      console.log('Info:', 'Abonnement annulé');
    }
  }, [searchParams]);

  // Fetch plans and subscriptions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plans
        const plansRes = await fetch(`${API}/subscriptions/plans`);
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);

        // Fetch user subscriptions if logged in
        if (user) {
          const subsRes = await fetch(`${API}/subscriptions`, {
            credentials: 'include'
          });
          const subsData = await subsRes.json();
          setSubscriptions(subsData.subscriptions || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      console.log('Error:', 'Veuillez vous connecter pour souscrire');
      return;
    }

    setSubscribing(planId);
    try {
      const response = await fetch(`${API}/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan: planId,
          hub_serial: hubSerial || null,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.log('Error:', data.detail || 'Erreur lors de la création de l\'abonnement');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Voulez-vous vraiment annuler cet abonnement ? Il restera actif jusqu\'à la fin de la période en cours.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Success:', data.message);
        // Refresh subscriptions
        const subsRes = await fetch(`${API}/subscriptions`, { credentials: 'include' });
        const subsData = await subsRes.json();
        setSubscriptions(subsData.subscriptions || []);
      } else {
        console.log('Error:', data.detail || 'Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  const handleManageSubscriptions = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.log('Error:', data.detail || 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      canceled: 'bg-red-500/20 text-red-400 border-red-500/30',
      past_due: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      trialing: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    const labels = {
      active: 'Actif',
      canceled: 'Annulé',
      past_due: 'Paiement en retard',
      trialing: 'Période d\'essai'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-fgs-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fgs-bg py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-fgs-primary/10 border border-fgs-primary/30 rounded-full px-4 py-2 mb-4">
            <Wifi className="w-4 h-4 text-fgs-primary" />
            <span className="text-fgs-primary text-sm font-medium">Forfaits 4G Ajax Systems</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Abonnements 4G pour votre centrale
          </h1>
          <p className="text-fgs-text-muted max-w-2xl mx-auto">
            Gardez votre système Ajax connecté en permanence grâce à notre forfait 4G. 
            Connexion cellulaire sécurisée, même en cas de coupure internet.
          </p>
        </div>

        {/* User's Active Subscriptions */}
        {user && subscriptions.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Mes abonnements</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManageSubscriptions}
                className="border-fgs-border text-white hover:bg-fgs-surface"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Gérer sur Stripe
              </Button>
            </div>
            <div className="grid gap-4">
              {subscriptions.map((sub) => (
                <Card key={sub.subscription_id} className="bg-fgs-surface border-fgs-border">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-fgs-primary/20 flex items-center justify-center">
                          <Wifi className="w-6 h-6 text-fgs-primary" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{sub.plan}</h3>
                          <p className="text-fgs-text-muted text-sm">
                            {sub.amount}€/{sub.interval === 'month' ? 'mois' : 'an'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(sub.status)}
                        <div className="text-right">
                          <p className="text-fgs-text-muted text-xs">Prochaine échéance</p>
                          <p className="text-white text-sm">
                            {new Date(sub.current_period_end).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        {sub.status === 'active' && !sub.cancel_at_period_end && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelSubscription(sub.subscription_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        {sub.cancel_at_period_end && (
                          <span className="text-yellow-400 text-xs">Annulation programmée</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Hub Serial Input */}
        {user && (
          <div className="mb-8 max-w-md mx-auto">
            <Card className="bg-fgs-surface border-fgs-border">
              <CardContent className="p-4">
                <Label htmlFor="hub-serial" className="text-white text-sm mb-2 block">
                  Numéro de série du Hub (optionnel)
                </Label>
                <Input
                  id="hub-serial"
                  placeholder="Ex: 00:00:00:00:00:00"
                  value={hubSerial}
                  onChange={(e) => setHubSerial(e.target.value)}
                  className="bg-fgs-bg border-fgs-border text-white"
                />
                <p className="text-fgs-text-muted text-xs mt-2">
                  Retrouvez-le dans l'application Ajax ou sur l'étiquette du Hub
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`bg-fgs-surface border-2 transition-all ${
                plan.id === 'annual' 
                  ? 'border-fgs-primary shadow-lg shadow-fgs-primary/20' 
                  : 'border-fgs-border hover:border-fgs-border-hover'
              }`}
            >
              {plan.id === 'annual' && (
                <div className="bg-fgs-primary text-white text-center py-2 text-sm font-medium">
                  Économisez {plan.savings}€/an
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-white text-xl">{plan.name}</CardTitle>
                <CardDescription className="text-fgs-text-muted">
                  {plan.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">{plan.price.toFixed(2)}€</span>
                    <span className="text-fgs-text-muted">/{plan.interval === 'month' ? 'mois' : 'an'}</span>
                  </div>
                  <p className="text-fgs-text-muted text-sm mt-1">
                    {plan.price_ht.toFixed(2)}€ HT + TVA {plan.tva_rate}%
                  </p>
                </div>
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-fgs-text">
                      <Check className="w-5 h-5 text-fgs-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className={`w-full ${
                    plan.id === 'annual'
                      ? 'bg-fgs-primary hover:bg-fgs-primary-hover text-white'
                      : 'bg-fgs-surface-highlight hover:bg-fgs-border text-white border border-fgs-border'
                  }`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribing === plan.id || !user}
                >
                  {subscribing === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirection...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {user ? 'Souscrire' : 'Connexion requise'}
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-12 max-w-2xl mx-auto">
          <Card className="bg-fgs-bg border-fgs-border">
            <CardContent className="p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-fgs-primary" />
                Comment ça marche ?
              </h3>
              <ol className="space-y-3 text-fgs-text-muted">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-fgs-primary/20 text-fgs-primary text-sm flex items-center justify-center flex-shrink-0">1</span>
                  <span>Choisissez votre forfait (mensuel ou annuel)</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-fgs-primary/20 text-fgs-primary text-sm flex items-center justify-center flex-shrink-0">2</span>
                  <span>Procédez au paiement sécurisé via Stripe</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-fgs-primary/20 text-fgs-primary text-sm flex items-center justify-center flex-shrink-0">3</span>
                  <span>Recevez votre carte SIM sous 24-48h</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-fgs-primary/20 text-fgs-primary text-sm flex items-center justify-center flex-shrink-0">4</span>
                  <span>Insérez-la dans votre Hub Ajax et c'est parti !</span>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
